import React, { useEffect, useMemo, useState } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import DocumentCard from '../components/shared/DocumentCard';
import confirmEmailImage from '../assets/images/confirm-email.png';
import CreateDocumentModal from '../components/modals/createDocumentModal';
import Step1ImportDocModal from '../components/modals/import-document/step1-import-doc-modal';
import CreateDocIcon from '../assets/icons/createDocIcon';
import ImportDocIcon from '../assets/icons/importDocIcon';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../components/AuthProvider';
import { DocumentService } from '../services/DocumentService';
import { Toast } from '../components/shared/Toast';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { QuickTourTooltip } from '../components/shared/QuickTourTooltip';

export default function UserDashboardPage() {
	const [query, setQuery] = useState('');
	const [documents, setDocuments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [refreshKey, setRefreshKey] = useState(0);
	const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] = useState(false);
	const [isStep2CreateDocumentModalOpen, setIsStep2CreateDocumentModalOpen] = useState(false);
	const [isImportDocModalOpen, setIsImportDocModalOpen] = useState(false);
	const [tourRun, setTourRun] = useState(false);
	const navigate = useNavigate();
	const { user } = useAuthContext();

	const tourSteps = useMemo(
		() => [
			{
				target: '#quick-actions',
				placement: 'bottom',
				spotlightPadding: 12,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">Quick actions</h3>
						<p className="text-sm text-customGray">
							Start new documents, browse templates, or import your existing files here.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			},
			{
				target: '#recent-documents',
				placement: 'bottom',
				spotlightPadding: 12,
				content: (
					<div className="text-left">
						<h3 className="text-dark-blue font-semibold mb-1">Recent documents</h3>
						<p className="text-sm text-customGray">
							Here you can view all your documents you've worked on previously.
						</p>
					</div>
				),
				disableBeacon: true,
				hideCloseButton: true
			}
		],
		[]
	);

	const handleJoyrideCallback = (data) => {
		const { status } = data || {};
		const element = document.getElementById('main_dashboard');
		const mainEl = element ? element.querySelector('main') : null;
		if (data?.index === 1) {
			element.classList.remove('lg:h-screen');
		}
		if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
			setTourRun(false);
			element.classList.add('lg:h-screen');
			// Joyride's inline overflow or any inline styles are removed
			const cleanup = () => {
				if (mainEl) {
					if (mainEl.style && mainEl.style.removeProperty) {
						mainEl.style.removeProperty('overflow');
					}
					if (mainEl.hasAttribute('style')) {
						mainEl.removeAttribute('style');
					}
				}
			};
			cleanup();
			// setTimeout(cleanup, 0);
		}
	};

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		return documents.filter((doc) => {
			const title = doc?.title || '';
			const matchesQuery = !q ? true : title.toLowerCase().includes(q);
			return matchesQuery;
		});
	}, [query, documents]);

	useEffect(() => {
		let mounted = true;
		async function loadRecent() {
			try {
				setLoading(true);
				setError('');
				const data = await DocumentService.fetchDocuments({ limit: 8 });
				const apiDocs = Array.isArray(data?.documents) ? data.documents : [];
				if (mounted) {
					setDocuments(apiDocs);
				}
			} catch (e) {
				const message =
					e?.response?.data?.message || e?.message || 'Failed to load recent documents';
				if (mounted) setError(message);
				Toast.error(message);
			} finally {
				if (mounted) setLoading(false);
			}
		}
		loadRecent();
		return () => {
			mounted = false;
		};
	}, [refreshKey]);

	// Fix scroll activation when transitioning from 0 documents to having documents
	useEffect(() => {
		if (!loading && documents.length > 0) {
			// Use requestAnimationFrame to ensure DOM has fully rendered
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					const mainEl = document.querySelector('main');
					const container = document.getElementById('main_dashboard');

					if (mainEl && container) {
						// Force complete reflow by manipulating both container and main element
						const originalMainDisplay = mainEl.style.display;

						// Force reflow
						mainEl.style.display = 'none';
						void container.offsetHeight; // Trigger reflow
						mainEl.style.display = originalMainDisplay || '';

						// Ensure container maintains proper height class
						if (!container.classList.contains('lg:h-screen')) {
							container.classList.add('lg:h-screen');
						}

						// Force scroll recalculation by accessing scrollTop
						void mainEl.scrollTop;
					}
				});
			});
		}
	}, [loading, documents.length]);

	useEffect(() => {
		const onUpdated = () => setRefreshKey((k) => k + 1);
		window.addEventListener('documents:updated', onUpdated);
		return () => window.removeEventListener('documents:updated', onUpdated);
	}, []);
	// listen to event when it is triggered from teh navbar quick tour button click
	useEffect(() => {
		const startTour = () => {
			try {
				const firstTarget = tourSteps?.[0]?.target;
				if (firstTarget) {
					const node = document.querySelector(firstTarget);
					if (node && typeof node.scrollIntoView === 'function') {
						node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
					}
				}
			} catch {}
			// Give the browser a tick to settle the scroll before starting the tour
			setTimeout(() => setTourRun(true), 150);
		};
		window.addEventListener('dashboard-tour:start', startTour);
		return () => window.removeEventListener('dashboard-tour:start', startTour);
	}, [tourSteps]);

	const handleCreateNew = () => {
		setIsCreateDocumentModalOpen(true); // Open modal
	};
	const handleImport = () => {
		setIsImportDocModalOpen(true);
	};

	const handleDocumentCreation = (documentId) => {
		// Assuming documentId is returned from the modal after creation
		// Navigate to the DocumentEditorPage with isCreated=true in URL
		// navigate(`/document-editor/${documentId}?isCreated=true`);
	};

	return (
		<div className="bg-bgColor">
			<Joyride
				callback={handleJoyrideCallback}
				continuous
				run={tourRun}
				scrollOffset={80}
				scrollToFirstStep
				showSkipButton
				steps={tourSteps}
				tooltipComponent={QuickTourTooltip}
				styles={{ options: { zIndex: 10000, arrowColor: 'transparent' } }}
				locale={{ last: 'End', skip: 'Skip', next: 'Next', back: 'Previous' }}
			/>
			{/* Header */}
			<div className="mb-14 flex flex-col items-center justify-between md:flex-row">
				<div>
					<h1 className="text-main font-bold leading-100 text-dark-blue">
						Welcome, {user.firstName}
					</h1>
				</div>

				<div className="flex items-center gap-3 flex-col md:flex-row">
					{/* Search */}
					<div className="relative w-80 my-5 md:my-0">
						<input
							type="text"
							placeholder="Search document..."
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							className="h-[52px] w-full rounded-lg border border-lightGrey pl-4 pr-10 focus:outline-none focus:border-purple"
						/>
						<svg
							className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<circle cx="11" cy="11" r="8"></circle>
							<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
						</svg>
					</div>
					{/* <button type="button" onClick={() => setTourRun(true)} className="h-[40px] px-3 rounded-lg border border-lightGrey bg-white text-dark-blue text-sm">
						Start Tour
					</button> */}
				</div>
			</div>

			{/* Quick actions: Create & Import (centered, column) */}
			<div id="quick-actions" className="mb-12 grid grid-cols-1 gap-4 md:grid-cols-2">
				{/* Create new document */}
				<button
					type="button"
					onClick={handleCreateNew}
					className="group flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-[#EDEDED] border-dashed bg-white p-10 text-center transition hover:shadow-sm"
				>
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#B67FE2]">
						{/* plus icon */}
						<CreateDocIcon width={24} height={28} className="inline-block text-white " />
					</div>
					<div>
						<div className="text-dark-blue font-semibold text-base">Create new document</div>
						<div className="text-customGray text-sm">
							Begin your document — we’ll build the complete APA-style structure for you.
						</div>
					</div>
				</button>

				{/* Import documents */}
				<button
					type="button"
					onClick={handleImport}
					className="group flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-[#EDEDED] border-dashed bg-white p-10 text-center transition hover:shadow-sm"
				>
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F3EEFF]">
						{/* import icon */}
						<ImportDocIcon width={24} height={28} className="inline-block text-dark-blue " />
					</div>
					<div>
						<div className="text-dark-blue font-semibold text-base">Import document</div>
						<div className="text-customGray text-sm">
							Upload your draft — we’ll shape it into a complete APA-style paper.
						</div>
					</div>
				</button>
			</div>

			<div id="recent-documents">
				{/* Recent documents header */}
				<div className="mb-8 flex items-center justify-between">
					{loading ? (
						<>
							<Skeleton
								width={250}
								height={20}
								baseColor="#E5E7EB"
								highlightColor="#F3F4F6"
								duration={1.2}
								className="rounded"
							/>
						</>
					) : (
						<>
							<h2 className="text-dark-blue text-[22px] font-medium leading-100">
								Recent {filtered.length === 1 ? 'Document' : 'Documents'}
							</h2>
						</>
					)}
					<a
						href="my-documents"
						className="inline-flex items-center gap-2 text-gray text-20 underline leading-24"
					>
						View all documents
					</a>
				</div>

				{/* Cards */}
				<div
					className="mb-[50px] grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
					ref={(el) => {
						// Force scroll recalculation when grid renders with documents
						if (el && !loading && documents.length > 0) {
							setTimeout(() => {
								const mainEl = document.querySelector('main');
								if (mainEl) {
									mainEl.style.overflowY = 'auto';
								}
							}, 100);
						}
					}}
				>
					{loading &&
						Array.from({ length: 8 }).map((_, idx) => (
							<div
								key={idx}
								className="flex flex-col items-start gap-4 sm:gap-5 w-full p-3 md:p-4 lg:p-[15px] pb-4 md:pb-5 rounded-[14px] sm:rounded-[18px] border border-[#F0F0F0] bg-white"
							>
								<div className="w-full h-32 sm:h-[150px] rounded-[14px] sm:rounded-[18px] overflow-hidden">
									<Skeleton
										height="100%"
										width="100%"
										baseColor="#E5E7EB"
										highlightColor="#F3F4F6"
										duration={1.2}
									/>
								</div>
								<div className="w-full flex justify-between items-start gap-2">
									<div className="w-3/4">
										<Skeleton height={20} width="80%" />
									</div>
									<div className="w-6 h-6">
										<Skeleton circle height={24} width={24} />
									</div>
								</div>
								<div className="w-full">
									<Skeleton count={3} height={12} className="mb-1" />
								</div>
								<div className="w-full flex items-center justify-between">
									<div className="flex gap-2">
										<Skeleton height={28} width={90} />
										<Skeleton height={28} width={110} />
									</div>
									<Skeleton circle height={32} width={32} />
								</div>
							</div>
						))}

					{!loading && error && (
						<div className="col-span-full flex items-center justify-center rounded-xl border border-dashed border-red-200 p-10 text-red-600">
							{error}
						</div>
					)}

					{!loading &&
						!error &&
						filtered.map((doc) => {
							const id = doc?._id;
							const imageSrc = doc?.format?.formatpictureurl || confirmEmailImage;
							const formatName = doc?.format?.name || '';
							const title = doc?.title || 'Untitled Document';
							const description = doc?.description;
							const status = doc?.status || '';
							return (
								<DocumentCard
									key={id}
									documentId={id}
									imageSrc={imageSrc}
									format={formatName}
									documentType={doc?.documentType?.name || ''}
									title={title}
									description={description}
									status={status}
									updatedAt={doc?.updatedAt}
									onClick={() => navigate(`/document-editor/${id}?isCreated=true`)}
									onOpenEditor={() => navigate(`/document-editor/${id}?isCreated=true`)}
								/>
							);
						})}

					{!loading && !error && filtered.length === 0 && (
						<div className="col-span-full flex items-center justify-center rounded-xl border border-dashed border-gray-200 p-10 text-gray-500">
							{documents.length === 0 ? (
								<>
									No documents yet. Start fresh or upload a draft, WriteForm will handle the
									structure for you.
								</>
							) : (
								<>No documents match your search. Try a different search term.</>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Step 1: Import Document Modal */}
			<Step1ImportDocModal
				isOpen={isImportDocModalOpen}
				onClose={() => setIsImportDocModalOpen(false)}
				onNextStep={(file) => {
					setIsImportDocModalOpen(false); // Close Step1 modal
					setIsStep2CreateDocumentModalOpen(true); // Open Step2 modal
				}}
			/>

			{/* Step 2: Create Document Modal */}
			<CreateDocumentModal
				isOpen={isStep2CreateDocumentModalOpen}
				onClose={() => setIsStep2CreateDocumentModalOpen(false)}
				onDocumentCreated={handleDocumentCreation} // Pass the handler
				isImported={true} // <-- pass isImported true here
			/>

			{/* Step 2: Create Document Modal for "Create New" */}
			<CreateDocumentModal
				isOpen={isCreateDocumentModalOpen}
				onClose={() => setIsCreateDocumentModalOpen(false)}
				onDocumentCreated={handleDocumentCreation} // Pass the handler
				isImported={false}
			/>
		</div>
	);
}
